import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { booksAPI } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { GbrPage, PublicTop, SiteFooter, Tabs, type GbrMode } from '../components/gbr/Shell';

const f = (n: number) => n.toLocaleString('en-US');

const GENRES = ['Fiction', 'Mystery & Thriller', 'Fantasy', 'Science Fiction', 'Horror'];

/** Reward badge from the live campaign prices (what a reader receives when picking the book). */
function badge(b: any): string {
  const act = (b.reviewCampaigns || []).filter((c: any) => c.isActive);
  const cr: number[] = act.map((c: any) => c.costCredits || 0).filter((x: number) => x > 0);
  const gm: number[] = act.map((c: any) => c.costGems || 0).filter((x: number) => x > 0);
  if (cr.length) {
    const max = Math.max(...cr);
    return `+${new Set(cr).size > 1 ? 'up to ' : ''}${f(max)} Bookmarks`;
  }
  if (gm.length) return `+${f(Math.max(...gm))} Golden Quills`;
  return '';
}

export default function Home() {
  const { isAuthenticated } = useAuthStore();
  const [mode, setMode] = useState<GbrMode>('r');
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  const { data: stats } = useQuery({
    queryKey: ['public-stats'],
    queryFn: async () => {
      const res = await fetch('/api/public/stats');
      const data = await res.json();
      return data.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  const { data: lib } = useQuery({
    queryKey: ['landing-library'],
    queryFn: () => booksAPI.getLibrary({ limit: 100 }),
    staleTime: 1000 * 60 * 5,
  });
  const open = useMemo(
    () => (lib || []).filter((b: any) => (b.reviewCampaigns || []).some((c: any) => c.isActive)),
    [lib],
  );
  const rail = open.slice(0, 6);

  // Review platforms (backend flag FEATURE_REVIEW_PLATFORMS): strip only shows when the flag is on
  const { data: platformInfo } = useQuery({
    queryKey: ['review-platforms'],
    queryFn: () => booksAPI.getReviewPlatforms(),
    staleTime: 5 * 60_000,
  });
  const platforms: { id: string; label: string }[] = platformInfo?.enabled ? platformInfo.platforms : [];

  // scroll-reveal for the static blocks (the dynamic rail needs none)
  useEffect(() => {
    const els = document.querySelectorAll('.gbr .rv');
    if (!('IntersectionObserver' in window)) { els.forEach((e) => e.classList.add('in')); return; }
    const o = new IntersectionObserver((es) => es.forEach((x) => {
      if (x.isIntersecting) { x.target.classList.add('in'); o.unobserve(x.target); }
    }), { rootMargin: '0px 0px -8% 0px' });
    els.forEach((e) => o.observe(e));
    return () => o.disconnect();
  }, []);

  // the hash target is rendered after load, so the browser's own jump misses it
  useEffect(() => {
    if (window.location.hash !== '#for-authors') return;
    const go = () => document.getElementById('for-authors')?.scrollIntoView({ block: 'center', behavior: 'instant' as ScrollBehavior });
    const t = [setTimeout(go, 300), setTimeout(go, 1200)];
    return () => t.forEach(clearTimeout);
  }, []);

  const search = (e: FormEvent) => {
    e.preventDefault();
    navigate(q.trim() ? `/library?q=${encodeURIComponent(q.trim())}` : '/library');
  };

  return (
    <>
      <Helmet>
        <title>GetBookReviews: One Campaign, Every Review Platform for Indie Authors</title>
        <meta
          name="description"
          content="One campaign, every review platform: reach real readers for Amazon, Goodreads, StoryGraph, LibraryThing and BookBub. No subscription, no review swaps, no paid reviews."
        />
        <meta
          name="keywords"
          content="how to get Amazon reviews, KDP book reviews, free book reviews, Amazon KDP reviews, self-publishing reviews, indie author reviews, ARC readers, ARC platform, advance reader copy, book review service, Kindle reviews, free ARC service, Amazon TOS compliant reviews, honest book reviews, BookSprout alternative, NetGalley alternative, Pubby alternative, book marketing, book promotion, get reviews for my book, how to get book reviews on Amazon, KDP marketing, self-published book reviews, indie book reviews"
        />
        <link rel="canonical" href="https://getbookreviews.org" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="GetBookReviews: One Campaign, Every Review Platform for Indie Authors" />
        <meta
          property="og:description"
          content="One campaign, every review platform: reach real readers for Amazon, Goodreads, StoryGraph, LibraryThing and BookBub. No subscription, no review swaps, no paid reviews."
        />
        <meta property="og:url" content="https://getbookreviews.org" />
        <meta property="og:image" content="https://getbookreviews.org/og-image.jpg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="GetBookReviews: One Campaign, Every Review Platform for Indie Authors" />
        <meta
          name="twitter:description"
          content="One campaign, every review platform: reach real readers for Amazon, Goodreads, StoryGraph, LibraryThing and BookBub. No subscription, no review swaps, no paid reviews."
        />
        <meta name="twitter:image" content="https://getbookreviews.org/og-image.jpg" />
        
        {/* Schema.org - WebApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'GetBookReviews',
            alternateName: 'Get Book Reviews',
            description: 'Free ARC platform for self-published authors to get authentic Amazon book reviews. The best free alternative to BookSprout and NetGalley. 100% Amazon TOS compliant.',
            url: 'https://getbookreviews.org',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'USD',
              description: 'Free to use - earn Bookmarks by reviewing books',
            },
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: '4.8',
              reviewCount: '127',
            },
            featureList: [
              'Free ARC distribution',
              'Amazon TOS compliant reviews',
              'Bookmark-based system',
              'No subscription fees',
              'Honest review guarantee',
              'Real reader community',
            ],
          })}
        </script>
        
        {/* Schema.org - HowTo for AI Search */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: 'How to Get Amazon Reviews for Your KDP Book',
            description: 'A step-by-step guide to getting authentic Amazon reviews for your self-published Kindle book using the free ARC platform GetBookReviews.org',
            totalTime: 'P14D',
            estimatedCost: {
              '@type': 'MonetaryAmount',
              currency: 'USD',
              value: '0',
            },
            step: [
              {
                '@type': 'HowToStep',
                position: 1,
                name: 'Create a Free Account',
                text: 'Sign up for free at GetBookReviews.org. No credit card required, no subscription fees.',
                url: 'https://getbookreviews.org/register',
              },
              {
                '@type': 'HowToStep',
                position: 2,
                name: 'Add Your Book',
                text: 'Upload your book cover, add your Amazon link, description, and upload your EPUB/PDF file.',
                url: 'https://getbookreviews.org/add-book',
              },
              {
                '@type': 'HowToStep',
                position: 3,
                name: 'Get Approved',
                text: 'Your book will be reviewed and approved within 24 hours by our moderation team.',
              },
              {
                '@type': 'HowToStep',
                position: 4,
                name: 'Earn Bookmarks',
                text: 'Review other authors books to earn Bookmarks. This builds community and earns you campaign Bookmarks.',
              },
              {
                '@type': 'HowToStep',
                position: 5,
                name: 'Launch Your Campaign',
                text: 'Use your Bookmarks to launch a review campaign. Choose from Standard (upload PDF/EPUB), Kindle (KU readers), Ebook, or Print campaigns.',
              },
              {
                '@type': 'HowToStep',
                position: 6,
                name: 'Get Reviews',
                text: 'Readers from the community will pick up your book, read it, and post honest reviews on Amazon within 2-4 weeks.',
              },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How do I get Amazon reviews for my KDP book?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'The best way to get Amazon reviews for your KDP book is through ARC (Advance Reader Copy) platforms like GetBookReviews.org. Sign up for free, upload your book, and connect with readers who will read your book and leave honest reviews on Amazon. This is 100% compliant with Amazon TOS.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is GetBookReviews compliant with Amazon TOS?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes. GetBookReviews follows the same model as Amazon Vine. We connect authors with readers who provide honest reviews in exchange for free ARC copies. This is explicitly allowed by Amazon as long as reviews are honest and unbiased. We never pay for positive reviews or require specific star ratings.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is GetBookReviews free?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes, GetBookReviews is 100% free. Unlike BookSprout or NetGalley, there are no subscription fees. Authors earn Bookmarks by reviewing other books, then use those Bookmarks to launch review campaigns for their own books. Readers also earn rewards for writing honest reviews.',
                },
              },
              {
                '@type': 'Question',
                name: 'What is the best free ARC service for self-published authors?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'GetBookReviews.org is one of the best free ARC services for self-published authors. Unlike paid alternatives like BookSprout ($10-30/month) or NetGalley ($450+/title), GetBookReviews is completely free. You earn Bookmarks by participating in the community and reviewing other books.',
                },
              },
              {
                '@type': 'Question',
                name: 'How long does it take to get book reviews on GetBookReviews?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'After your book is approved (within 24 hours), you can launch a review campaign immediately. Readers typically pick up books within 1-7 days, and reviews are submitted within 2-4 weeks depending on book length. The platform has an active community of readers looking for new books.',
                },
              },
              {
                '@type': 'Question',
                name: 'Can I get negative reviews on GetBookReviews?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes, and that is by design. GetBookReviews requires honest reviews - 1 to 5 stars are all equally valid. Reviewers earn the same Bookmarks regardless of the rating they give. This ensures authentic feedback and keeps the platform compliant with Amazon TOS.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is GetBookReviews better than BookSprout?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'GetBookReviews offers a free alternative to BookSprout. While BookSprout charges $10-30/month, GetBookReviews is completely free. Both platforms connect authors with ARC readers, but GetBookReviews uses a Bookmark-based system where authors earn Bookmarks by reviewing other books.',
                },
              },
              {
                '@type': 'Question',
                name: 'How do I get more reviews for my self-published book on Amazon?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'To get more reviews for your self-published book: 1) Use ARC platforms like GetBookReviews.org to connect with readers, 2) Add a review request at the end of your book, 3) Build an email list and ask subscribers to review, 4) Join author communities and ask readers for honest reviews. Never pay for reviews or offer incentives for positive reviews as this violates Amazon TOS.',
                },
              },
            ],
          })}
        </script>
      
        <link rel="preload" href="/fonts/manrope-latin-800-normal.woff2" as="font" type="font/woff2" crossOrigin="" />
        <link rel="preload" href="/fonts/manrope-latin-500-normal.woff2" as="font" type="font/woff2" crossOrigin="" />
      </Helmet>
      <GbrPage mode={mode}>
        <PublicTop />

        <div className="hero">
          <div className="w">
            <div className="tog" role="group" aria-label="I am a">
              <button type="button" aria-pressed={mode === 'r'} onClick={() => setMode('r')}>I read</button>
              <button type="button" aria-pressed={mode === 'w'} onClick={() => setMode('w')}>I write</button>
            </div>
            <div className="pr1">
              <h1>Find a free book. <em>Review it honestly.</em></h1>
              <p className="lead">Indie authors offer free copies. You pick one, read it, and share what you really thought &ndash; and collect Bookmarks or Golden Quills for every review.</p>
              <form className="search" onSubmit={search} role="search">
                <input name="q" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title, author or genre" aria-label="Search books" />
                <button className="btn p" type="submit">Browse</button>
              </form>
              <div className="gen">
                {GENRES.map((g) => <Link key={g} to={`/library?category=${encodeURIComponent(g)}`}>{g}</Link>)}
                <Link to="/library">All genres &rarr;</Link>
              </div>
            </div>
            <div className="pr2">
              <h1>One campaign, <em>every review platform.</em></h1>
              <p className="lead">Put your book in front of real readers and let them review it on Amazon, Goodreads, StoryGraph, LibraryThing or BookBub. Pay with Bookmarks &ndash; earn them by picking and reading other authors&rsquo; books, or use Golden Quills. No subscription, no review swaps, no promised ratings.</p>
              <Link className="btn y" to="/register">Create free account</Link>{' '}
              <a className="btn o" href="#how">How it works</a>
            </div>
            <div className="trust"><span>Free to join</span><span>Same reward for any star rating</span><span>No payment details</span></div>
          </div>
        </div>

        <main>
          <div className="w">
            <div className="nums" aria-label="Community numbers">
              <div><b>{stats ? f(stats.users) : '–'}</b><span>members</span></div>
              <div><b>{stats ? f(stats.books) : '–'}</b><span>books added</span></div>
              <div><b>{stats ? f(stats.reviews) : '–'}</b><span>reviews written</span></div>
              <div><b>{stats?.avgRating ? stats.avgRating.toFixed(1) : '–'}</b><span>average rating</span></div>
            </div>
            <p>Live numbers from our database, nothing rounded up.</p>
          </div>

          <section>
            <div className="w">
              <div className="hd">
                <div><h2>Free copies to pick from</h2><p className="sub">Real books from real authors in the library right now.</p></div>
                <Link className="btn o sm" to="/library">See all{open.length ? ` ${open.length}` : ''} books</Link>
              </div>
              <div className="rail" style={{ minHeight: 300 }}>
                {rail.map((b: any) => {
                  const id: string = b._id;
                  const t = `/covers-thumb/${id.slice(-8)}`;
                  return (
                    <Link className="bk" key={id} to={`/book/${id}`}>
                      <img
                        src={`${t}-180.webp`}
                        srcSet={`${t}-180.webp 180w,${t}-360.webp 360w`}
                        sizes="(max-width:900px) 45vw,180px"
                        width={180}
                        height={270}
                        loading="lazy"
                        decoding="async"
                        alt={`Cover of ${b.title}`}
                        onError={(e) => {
                          const im = e.currentTarget;
                          if (b.coverUrl && im.src !== b.coverUrl) { im.removeAttribute('srcset'); im.src = b.coverUrl; }
                        }}
                      />
                      <h3>{b.title}</h3>
                      <small>{b.author}</small>
                      {badge(b) && <span className="bdg">{badge(b)}</span>}
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>

          <section id="how" style={{ paddingTop: 0 }}>
            <div className="w">
              <h2>How it works</h2>
              <p className="sub">Use the switch at the top &ndash; the steps follow you.</p>
              <div className="steps sr">
                <div className="stp rv"><i>1</i><b>Join</b><p>One minute. Free, no subscription.</p></div>
                <div className="stp rv"><i>2</i><b>Choose</b><p>Pick a book you actually want to read.</p></div>
                <div className="stp rv"><i>3</i><b>Read</b><p>You have 14 days. Your reward lands when you pick the book.</p></div>
                <div className="stp rv"><i>4</i><b>Review</b><p>Post your honest review in time and keep the reward.</p></div>
              </div>
              <div className="steps sa">
                <div className="stp rv"><i>1</i><b>Create account</b><p>Free, under a minute.</p></div>
                <div className="stp rv"><i>2</i><b>Add your book</b><p>Cover, blurb, and how readers get a copy.</p></div>
                <div className="stp rv"><i>3</i><b>Get approved</b><p>Our team checks it within 24 hours.</p></div>
                <div className="stp rv"><i>4</i><b>Launch</b><p>Pay per reader with Bookmarks. 1,500 per standard review.</p></div>
              </div>
              {platforms.length > 0 && (
                <div className="plats">
                  <p>Reviews can be posted on</p>
                  <div>
                    {platforms.map((p) => (
                      <Link key={p.id} className={p.id} to={`/library?platform=${p.id}`} aria-label={`Books you can review on ${p.label}`}>{p.label}</Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section id="authors" style={{ paddingTop: 0 }}>
            <div className="w">
              <div className="split">
                <div className="pane rv">
                  <h2 style={{ fontSize: 30 }}>For readers</h2>
                  <p className="sub" style={{ marginBottom: 0 }}>Read more, discover more.</p>
                  <ul><li>Free copies from indie authors</li><li>Bookmarks or Golden Quills for every review</li><li>Any rating is rewarded the same</li><li>Hand a book back if life gets in the way</li></ul>
                  <Link className="btn p" to={isAuthenticated ? '/library' : '/register'}>{isAuthenticated ? 'Browse library' : 'Start reading'}</Link>
                </div>
                <div className="pane a rv" id="for-authors">
                  <h2 style={{ fontSize: 30 }}>For authors</h2>
                  <p className="sub" style={{ marginBottom: 0 }}>Get real readers for your book.</p>
                  <ul><li>Free account, no subscription</li><li>Readers choose your book from the library</li><li>Earn Bookmarks by picking and reading other authors&rsquo; books, then spend them on reader slots</li><li>Paid visibility is separate and never buys reviews</li></ul>
                  <Link className="btn y" to={isAuthenticated ? '/add-book' : '/register'}>{isAuthenticated ? 'Add a book' : 'Add my book'}</Link>{' '}
                  <Link className="btn sm" style={{ color: '#fff', textDecoration: 'underline' }} to="/advertise">Advertise</Link>
                </div>
              </div>
            </div>
          </section>

          <section style={{ paddingTop: 0 }}>
            <div className="w">
              <h2>Clear rules, written plainly</h2>
              <p className="sub">Four lines you can read in ten seconds.</p>
              <div className="rules">
                <div className="rl rv"><span>&#9733;</span><b>Any rating</b><p>1 to 5 stars, rewarded the same.</p></div>
                <div className="rl rv"><span>&#9993;</span><b>Free copy, said openly</b><p>Mention it when you post your review elsewhere.</p></div>
                <div className="rl rv"><span>&#9888;</span><b>No fake reviews, no swaps</b><p>Reviewing each other&rsquo;s books is blocked. Abuse ends accounts.</p></div>
                <div className="rl rv"><span>&#9202;</span><b>14 days</b><p>Hand the slot back if you can&rsquo;t finish.</p></div>
              </div>
            </div>
          </section>

          <section style={{ paddingTop: 0 }}>
            <div className="w faq">
              <h2>Quick answers</h2>
              <p className="sub">More in the <Link to="/faq">full FAQ</Link>.</p>
              <details><summary>Is it really free?</summary><p>Yes. No subscription, no per-review fee, no payment details to sign up.</p></details>
              <details><summary>What do I get for a review?</summary><p>Bookmarks or Golden Quills. The amount is shown on every book before you pick it. The rating you give does not change it.</p></details>
              <details><summary>Do I have to rate positively?</summary><p>No. Honest is the only rule.</p></details>
              <details><summary>Do I have to review to be an author?</summary><p>No. You can pay for reader slots with Golden Quills, or with Bookmarks earned from ads, boosts and referrals, without reviewing others. Picking and reading books is the fastest way to earn Bookmarks.</p></details>
            </div>
          </section>

          <section style={{ paddingTop: 0 }}>
            <div className="w">
              <div className="band">
                {isAuthenticated ? (<>
                  <div><h2>Find your next book.</h2><p>Browse free copies from indie authors and earn Bookmarks with honest reviews.</p></div>
                  <Link className="btn y" to="/library">Browse library</Link>
                </>) : (<>
                  <div><h2>Your next read is waiting.</h2><p>Free to join. Pick a book and start reading today.</p></div>
                  <Link className="btn y" to="/register">Join free</Link>
                </>)}
              </div>
            </div>
          </section>
        </main>

        <SiteFooter />
        <Tabs />
      </GbrPage>
    </>
  );
}
